import { INSERT_ORDERED_LIST_COMMAND, INSERT_UNORDERED_LIST_COMMAND, ListItemNode, ListNode, REMOVE_LIST_COMMAND } from '@lexical/list'
import { LexicalComposer } from '@lexical/react/LexicalComposer'
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'
import { ContentEditable } from '@lexical/react/LexicalContentEditable'
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary'
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin'
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin'
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin'
import { ListPlugin } from '@lexical/react/LexicalListPlugin'
import { LinkPlugin } from '@lexical/react/LexicalLinkPlugin'
import { $createHeadingNode, $createQuoteNode, HeadingNode, QuoteNode } from "@lexical/rich-text";
import { $generateHtmlFromNodes, $generateNodesFromDOM } from "@lexical/html";
import { $getRoot, $getSelection, $isRangeSelection, FORMAT_TEXT_COMMAND } from "lexical";
import { BoldIcon, CodeIcon, Heading1Icon, Heading2Icon, ItalicIcon, LinkIcon, ListIcon, ListOrderedIcon, QuoteIcon, StrikethroughIcon, UnderlineIcon, UnlinkIcon, } from 'lucide-react'
import { useCallback, useEffect, useRef } from 'react'

import { classNames } from '../../helpers'
import { LinkNode, AutoLinkNode, TOGGLE_LINK_COMMAND } from "@lexical/link";
import { AutoLinkPlugin, createLinkMatcherWithRegExp } from '@lexical/react/LexicalAutoLinkPlugin'
import { StyledLinkNode } from './StyledLinkNode'
// }

const theme = {
  paragraph: 'mb-2 text-gray-800',
  heading: {
    h1: 'text-2xl font-bold mb-2',
    h2: 'text-xl font-semibold mb-2',
    h3: 'text-lg font-medium mb-2',
  },
  quote: 'border-l-4 border-gray-300 pl-4 italic text-gray-600',
  list: {
    ul: 'list-disc list-inside mb-2',
    ol: 'list-decimal list-inside mb-2',
    listitem: 'mb-1',
  },
  link: 'text-blue-600 underline hover:text-blue-800',
  text: {
    bold: 'font-bold',
    italic: 'italic',
    underline: 'underline',
    strikethrough: 'line-through',
    code: 'bg-gray-100 text-red-500 font-mono px-1 py-0.5 rounded',
    link: 'text-blue-600 underline hover:text-blue-800',
  },
}

const editorConfig = {
  namespace: 'MyEditor',
  theme,
  onError(error) {
    console.error(error)
  },
  nodes: [HeadingNode, QuoteNode, ListNode, ListItemNode,
    LinkNode,
    // CustomLinkNode,
    // StyledLinkNode,
    AutoLinkNode
  ],
}

function Toolbar() {
  const [editor] = useLexicalComposerContext()

  return (
    <div className="flex flex-wrap gap-2 mb-2 border-b pb-2">
      <button
        title="Bold"
        onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'bold')}
        className="p-1 hover:bg-gray-100 rounded"
      >
        <BoldIcon size={18} />
      </button>
      <button
        title="Italic"
        onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'italic')}
        className="p-1 hover:bg-gray-100 rounded"
      >
        <ItalicIcon size={18} />
      </button>
      <button
        title="Underline"
        onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'underline')}
        className="p-1 hover:bg-gray-100 rounded"
      >
        <UnderlineIcon size={18} />
      </button>
      <button title="Insert Link" onClick={() => {
        const url = prompt('Enter URL:')
        if (!url) return
        editor.update(() => {
          const selection = $getSelection()
          if ($isRangeSelection(selection)) {
            editor.dispatchCommand(TOGGLE_LINK_COMMAND, {
              url,
              target: '_blank',
              rel: 'noopener noreferrer',
              style: 'color: blue;',
            })
          } else {
            alert('Please select some text to add a link.')
          }
        })
      }} className="p-1 hover:bg-gray-100 rounded">
        <LinkIcon size={18} />
      </button>
      <button title="Remove Link" onClick={() => {
        editor.update(() => {
          const selection = $getSelection()
          if ($isRangeSelection(selection)) {
            editor.dispatchCommand(TOGGLE_LINK_COMMAND, null)
          }
        })
      }} className="p-1 hover:bg-gray-100 rounded">
        <UnlinkIcon size={18} />
      </button>
      {/* <button title="Insert Link" onClick={() => {
        const url = prompt('Enter URL:')
        if (!url) return
        editor.update(() => {
          const selection = $getSelection()
          if ($isRangeSelection(selection)) {
            const linkNode = $createLinkNode(url)
            linkNode.setAttributes({ class: 'text-blue-500 underline' })
            linkNode.append(...selection.extract())
            selection.insertNodes([linkNode])
          } else {
            alert('Please select some text to add a link.')
          }
        })
      }} className="p-1 hover:bg-gray-100 rounded">
        <LinkIcon size={18} />
      </button>
      <button title="Remove Link" onClick={() => {
        editor.update(() => {
          const selection = $getSelection()
          if ($isRangeSelection(selection)) {
            const nodes = selection.getNodes()
            nodes.forEach(node => {
              if ($isLinkNode(node)) {
                node.replace(...node.getChildren())
              }
            })
          }
        })
      }} className="p-1 hover:bg-gray-100 rounded">
        <UnlinkIcon size={18} />
      </button> */}
      <button
        title="Strikethrough"
        onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'strikethrough')}
        className="p-1 hover:bg-gray-100 rounded"
      >
        <StrikethroughIcon size={18} />
      </button>
      <button
        title="Code"
        onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'code')}
        className="p-1 hover:bg-gray-100 rounded"
      >
        <CodeIcon size={18} />
      </button>
      <button
        title="Heading 1"
        onClick={() => editor.update(() => {
          const selection = $getSelection()
          if ($isRangeSelection(selection)) {
            selection.insertNodes([$createHeadingNode('h1')])
          }
        })}
        className="p-1 hover:bg-gray-100 rounded"
      >
        <Heading1Icon size={18} />
      </button>
      <button
        title="Heading 2"
        onClick={() => editor.update(() => {
          const selection = $getSelection()
          if ($isRangeSelection(selection)) {
            selection.insertNodes([$createHeadingNode('h2')])
          }
        })}
        className="p-1 hover:bg-gray-100 rounded"
      >
        <Heading2Icon size={18} />
      </button>
      <button
        title="Quote"
        onClick={() => editor.update(() => {
          const selection = $getSelection()
          if ($isRangeSelection(selection)) {
            selection.insertNodes([$createQuoteNode()])
          }
        })}
        className="p-1 hover:bg-gray-100 rounded"
      >
        <QuoteIcon size={18} />
      </button>
      <button
        title="Bullet List"
        onClick={() => editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined)}
        className="p-1 hover:bg-gray-100 rounded"
      >
        <ListIcon size={18} />
      </button>
      <button
        title="Numbered List"
        onClick={() => editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined)}
        className="p-1 hover:bg-gray-100 rounded"
      >
        <ListOrderedIcon size={18} />
      </button>
      <button
        title="Remove List"
        onClick={() => editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined)}
        className="p-1 hover:bg-gray-100 rounded"
      >
        <ListIcon size={18} className="text-red-400" />
      </button>
    </div>
  )
}
function HTMLExporter({ onChange }) {
  const [editor] = useLexicalComposerContext()

  const handleChange = useCallback(
    (editorState) => {
      editorState.read(() => {

        const root = $getRoot()
        const selection = $getSelection()
        const text = root.getTextContent()
        const html = $generateHtmlFromNodes(editor, null)
        if (onChange) onChange(editorState, html)
      })
    },
    [editor, onChange]
  )

  return <OnChangePlugin onChange={handleChange} />
}
function InitialContentPlugin({ initialHtml }) {
  const [editor] = useLexicalComposerContext()
  const didInit = useRef(false)

  useEffect(() => {
    if (!initialHtml || didInit.current) return

    editor.update(() => {
      try {
        const parser = new DOMParser()
        const dom = parser.parseFromString(initialHtml, 'text/html')
        const nodes = $generateNodesFromDOM(editor, dom)

        const root = $getRoot()
        root.clear()
        root.append(...nodes) // Make sure 'nodes' are Lexical Nodes
      } catch (err) {
        console.error('Error parsing initial HTML:', err)
      }
    })

    didInit.current = true
  }, [editor, initialHtml])

  return null
}


export default function TwTextEditer(props) {
  const { name = "", label = "", className = "", errors = {}, error = "", initialValue, onChange } = props
  let inputLableClass = "text-black/[0.6]"
  let inputClass = "text-gray-900 outline-gray-300 placeholder:text-gray-400 hover:outline-indigo-bz focus-within:outline-indigo-bz"

  if (name && errors[name] && errors[name] !== "") {
    props = {
      ...props,
      error: props.errors[name]
    };
  }
  if (props.error && props.error !== "") {
    inputLableClass = "text-red-600"
    inputClass = "border-red-300 outline-red-300 placeholder:border-red-300 focus-within:outline-red-600"
  }
  return (
    <LexicalComposer initialConfig={editorConfig}>
      {label && (<label htmlFor={name} className={classNames("block text-sm/6 font-medium", inputLableClass)}>
        {label}
      </label>)}
      <div className={classNames("border p-4 rounded-md bg-white min-h-[150px] text-base text-gray-800 w-full sm:w-full sm:max-w-sm md:max-w-lg", inputClass)}>
        <Toolbar />
        <RichTextPlugin
          contentEditable={
            <ContentEditable className="outline-none min-h-[150px] w-full prose max-w-none" />
          }
          placeholder={<div className="text-gray-400">Start typing...</div>}
          ErrorBoundary={LexicalErrorBoundary}
        />
        <HistoryPlugin />
        <ListPlugin />
        <LinkPlugin />
        <AutoLinkPlugin matchers={[createLinkMatcherWithRegExp(/https?:\/\/[^\s]+/g, (text) => text)]} />
        
        {/* <AutoLinkPlugin
          matchers={[
            createLinkMatcherWithRegExp(
              /https?:\/\/[^\s]+/g,
              (text) => text
            )
          ]}
        /> */}
        <HTMLExporter onChange={onChange} />
        <InitialContentPlugin initialHtml={initialValue} />
      </div>
    </LexicalComposer>
  )
}
